import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { Close } from "@dreamhub/designsystem/icons";
import RevenueItemsTable from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/components/RevenueItemsTable";
import { formatDateShort } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";
import { ContractData } from "@/pages/contracts/types";
import { toDateOnlyISO } from "@/utils/datetime";
import { useUndoToast } from "@/hooks/useUndoToast";
import ShiftDatesMenu from "./ShiftDatesMenu";
import DuplicateBulkActionsBar from "./DuplicateBulkActionsBar";
import DuplicateCalculatedValues from "./DuplicateCalculatedValues";
import { DuplicateContractDraft } from "./useDuplicateContractDraft";
import {
  ContractFinancialEstimate,
  estimateFinancials,
  financialDeltas
} from "./financials";
import { DraftRevenueItem } from "@/pages/contracts/components/termsDraft/types";
import { DateShift } from "./types";

interface DuplicateServicesSectionProps {
  draft: DuplicateContractDraft;
  sourceContract: ContractData;
  productId: string | null;
}

const DuplicateServicesSection: React.FC<DuplicateServicesSectionProps> = ({
  draft,
  sourceContract,
  productId
}) => {
  const { t } = useTranslation();
  const { showUndoToast } = useUndoToast<DraftRevenueItem[]>("bottom-left");

  const activeItems = useMemo(
    () => draft.items.filter((item) => !item.draftDeleted),
    [draft.items]
  );
  const activeIds = useMemo(
    () => activeItems.map((item) => item.id),
    [activeItems]
  );

  // Source column shows the backend's authoritative values; New is derived
  // from them plus the locally-estimated delta so an untouched draft shows
  // Source and New as identical.
  const sourceValues: ContractFinancialEstimate = useMemo(
    () => ({
      mrr: sourceContract.calculatedMrrLocal ?? 0,
      cmrr:
        sourceContract.calculatedCmrrLocal ??
        sourceContract.calculatedMrrLocal ??
        0,
      acv: sourceContract.calculatedAnnualContractValueLocal ?? 0,
      tcv: sourceContract.calculatedTotalContractValueLocal ?? 0
    }),
    [sourceContract]
  );

  const deltas = useMemo(
    () =>
      financialDeltas(
        estimateFinancials(draft.items),
        estimateFinancials(sourceContract.revenueItems)
      ),
    [draft.items, sourceContract.revenueItems]
  );

  const showShiftError = (retry: () => void) => {
    toast.error(
      <ToastBody
        title={t("contracts.duplicate.shiftDates.error")}
        actions={
          <>
            <Button
              variant="secondaryNoBg"
              className="text-primary"
              onClick={() => {
                toast.dismiss();
                retry();
              }}
            >
              {t("contracts.duplicate.shiftDates.tryAgain")}
            </Button>
            <IconButton
              size="md"
              variant="iconNoBg"
              iconColor="secondary"
              onClick={() => toast.dismiss()}
            >
              <Close />
            </IconButton>
          </>
        }
      />,
      { icon: null, position: "bottom-left" }
    );
  };

  const shiftableTargets = (ids: string[]) =>
    draft.items.filter(
      (item) =>
        ids.includes(item.id) &&
        !item.draftDeleted &&
        (item.serviceStartDate || item.billingStartDate)
    );

  const servicesLabel = (ids: string[]) =>
    ids.length === activeIds.length ? null : String(ids.length);

  const handleShift = (ids: string[], shift: DateShift) => {
    if (shiftableTargets(ids).length === 0) {
      showShiftError(() => handleShift(ids, shift));
      return;
    }
    const snapshot = draft.items;
    draft.shiftDates(ids, shift);
    draft.setSelected([]);
    const count = servicesLabel(ids);
    const duration = t(`contracts.duplicate.shiftDates.presets.${shift.unit}`, {
      count: shift.amount
    }).toLowerCase();
    showUndoToast(
      count
        ? t("contracts.duplicate.shiftDates.movedForward", {
            count: Number(count),
            duration
          })
        : t("contracts.duplicate.shiftDates.movedForwardAll", { duration }),
      snapshot,
      async (previousItems) => draft.replaceItems(previousItems)
    );
  };

  const handlePickStartDate = (ids: string[], date: Date) => {
    if (shiftableTargets(ids).length === 0) {
      showShiftError(() => handlePickStartDate(ids, date));
      return;
    }
    const snapshot = draft.items;
    // The picker hands back the chosen day at local midnight, whose UTC instant
    // is the day before anywhere ahead of UTC. Send the day itself, which is
    // what the toast below already reports.
    draft.setStartDate(ids, toDateOnlyISO(date));
    draft.setSelected([]);
    const count = servicesLabel(ids);
    const formattedDate = formatDateShort(toDateOnlyISO(date));
    showUndoToast(
      count
        ? t("contracts.duplicate.shiftDates.movedToStart", {
            count: Number(count),
            date: formattedDate
          })
        : t("contracts.duplicate.shiftDates.movedToStartAll", {
            date: formattedDate
          }),
      snapshot,
      async (previousItems) => draft.replaceItems(previousItems)
    );
  };

  const isEmpty = draft.items.length === 0;

  const handleToggleSelectAll = () => {
    const allSelected =
      activeIds.length > 0 &&
      activeIds.every((id) => draft.selectedIds.includes(id));
    draft.setSelected(allSelected ? [] : activeIds);
  };

  return (
    <div
      className="flex flex-col items-center"
      data-test="duplicate-services-section"
    >
      <div className="flex items-start justify-between w-full mb-2">
        <div className="flex flex-col gap-1">
          <Text variant="smSubtitle">
            {t("contracts.duplicate.terms.title")}
          </Text>
          {isEmpty ? null : (
            <Text variant="bodySecondary">
              {t("contracts.duplicate.terms.subtitle")}
            </Text>
          )}
        </div>
        {isEmpty ? null : (
          <ShiftDatesMenu
            trigger={
              <Button
                variant="secondary"
                data-test="duplicate-shift-all-button"
              >
                {t("contracts.duplicate.terms.shiftAll")}
              </Button>
            }
            onShift={(shift) => handleShift(activeIds, shift)}
            onPickStartDate={(date) => handlePickStartDate(activeIds, date)}
          />
        )}
      </div>
      <div className="relative z-10 flex flex-col w-full bg-surface-elevation1 rounded-xl border border-border-border1 overflow-hidden">
        <RevenueItemsTable
          revenueItems={draft.items}
          productId={productId ?? undefined}
          currency={sourceContract.currency}
          showStatusColumn={false}
          draft={{
            baseline: draft.baseline,
            selectedIds: draft.selectedIds,
            onToggleSelect: draft.toggleSelect,
            onToggleSelectAll: handleToggleSelectAll,
            onDeleteItem: (id) => draft.deleteItems([id]),
            onRestoreItem: draft.restoreItem,
            onAddItem: draft.addItem,
            onUpdateItem: draft.updateItem
          }}
        />
      </div>
      <DuplicateCalculatedValues
        source={sourceValues}
        deltas={deltas}
        currency={sourceContract.currency}
        onlyTotal={isEmpty}
      />
      {draft.selectedIds.length > 0 ? (
        <DuplicateBulkActionsBar
          selectedCount={draft.selectedIds.length}
          totalCount={activeIds.length}
          onShift={(shift) => handleShift(draft.selectedIds, shift)}
          onPickStartDate={(date) =>
            handlePickStartDate(draft.selectedIds, date)
          }
          onDelete={() => draft.deleteItems(draft.selectedIds)}
          onClearSelection={() => draft.setSelected([])}
        />
      ) : null}
    </div>
  );
};

export default DuplicateServicesSection;
